import React, { useMemo, useState } from "react";
import { Screen } from '~common/screen';
import { Styled } from './styled'
import { StyledScreen, IProps as IScreenProps } from '~common/screen'
import { OnlinepbxCheckbox } from '@onpbx/onlinepbx-components'
import MinusIcon from '~images/minus-v2.svg'
import PlusIcon from '~images/plus-v2.svg'
import MultSignIcon from '~images/close-icon.svg'
import { dividedByThree } from '~helpers/constant'
import { Accordion, IItem as IAccordionItem } from "~common/accordion";
import {
  ADDITIONAL_MODULES,
  MAX_COUNT_USERS,
  MIN_COUNT_USERS,
} from './constants'
import { IPrice } from "~/types/IPrice";
import { ICountryItem } from "~/types/ICountryItem";
import { useAppSelector } from "~/hooks/redux";

interface IProps {
  screenProps?: IScreenProps,
  style?: any
  isLegalPerson?: boolean;
  setIsLegalPerson: any;
  services?: IPrice[];
}

interface ICurrency {
  [key: string]: string;
}

const PriceCalculateScreenV2: React.FC<IProps> = (props) => {
  const { isLegalPerson, services } = props;
  const { country } = useAppSelector(state => state.countryReducer);

  const getPrice = (type: string, services: IPrice[], selectedCountry: ICountryItem, isLegalPerson: boolean) => {
      const service = services?.find((service) => service.type === type);
      const cost = service?.cost_in_currency_per_day.find((item) =>
          item.currency === ((isLegalPerson ? 'RUB' : selectedCountry?.currency)));
      return Math.round(cost?.cost/100 * service?.step_period_count)
  }

  const pricePerUser = getPrice('USER', services, country, isLegalPerson);

  const [isFocus, setFocus] = React.useState<boolean>(false)
  const [countUser, setCount] = React.useState<number>(MIN_COUNT_USERS)
  const [selectedModules, setSelectedModules] = useState(
    ADDITIONAL_MODULES.reduce((acc: any, curr: any) => {
      acc[curr.label] = false;
      return acc
    }, {})
  );

  const currencyObj: ICurrency = {
    RU: '₽',
    UZ: 'UZS',
    KZ: '₸',
  };

  const currency = currencyObj[country?.value] || '₽';

  const input = React.useRef(null)
  const reg = /^\d*$/

  const handleChange = (count: number) => setCount(count)
  const handleFocus = () => setFocus(!isFocus)

  const handleMinusCount = () => {
    if (countUser === 1) return
    const result = countUser - 1
    setCount(result)
  }
  const handlePlusCount = () => {
    if (countUser > MAX_COUNT_USERS) return
    const result = countUser + 1
    setCount(result)
  }

  const handlePress = (evt: any) => {
    if (evt.keyCode === 13) {
      if (countUser === 0) {
        setCount(MIN_COUNT_USERS)
      }
      if (countUser > MAX_COUNT_USERS) {
        setCount(MAX_COUNT_USERS)
      }
      evt.target.blur()
    }
  }

  const calculate = useMemo(() => {
    const modulesPrice = ADDITIONAL_MODULES.reduce((acc: any, curr: any) => {
      acc += selectedModules[curr.label] ? getPrice(curr.label, services, country, isLegalPerson) : 0

      return acc
    }, 0)

    if (countUser === 0) return pricePerUser + modulesPrice
    if (countUser > MAX_COUNT_USERS) return pricePerUser * MAX_COUNT_USERS + modulesPrice
    return pricePerUser * countUser + modulesPrice
  }, [selectedModules, countUser, pricePerUser, MAX_COUNT_USERS, ADDITIONAL_MODULES])

  const onSelectModule = (module: any) => {
    setSelectedModules({ ...selectedModules, [module.label]: !selectedModules[module.label] })
  }

  const [activeQuestionIndex, setActiveQuestionIndex] = useState(2)
  const questions: IAccordionItem[] = [
    {
      title: 'Что такое рабочее место?',
      text: 'Рабочее место (пользователь) — это добавочный номер, с которого сотрудник совершает и принимает звонки через различные устройства (ПК, мобильное приложение, виджет в CRM-системе, IP-телефон).'
    },
    {
      title: 'Что входит в цену одного рабочего места?',
      text: (
          <Styled.AccordionQuestionListContainer>
            <Styled.AccordionQuestionList>
              <Styled.AccordionQuestionListItem>Настройка аккаунта и подключение номеров</Styled.AccordionQuestionListItem>
              <Styled.AccordionQuestionListItem>30+ готовых интеграций с CRM</Styled.AccordionQuestionListItem>
              <Styled.AccordionQuestionListItem>Мобильное приложение для звонков</Styled.AccordionQuestionListItem>
              <Styled.AccordionQuestionListItem>Мобильное приложение для управления аккаунтом</Styled.AccordionQuestionListItem>
            </Styled.AccordionQuestionList>
            <Styled.AccordionQuestionList>
              <Styled.AccordionQuestionListItem>Виджет обратного звонка для сайта</Styled.AccordionQuestionListItem>
              <Styled.AccordionQuestionListItem>Виджет amoCRM для звонков</Styled.AccordionQuestionListItem>
              <Styled.AccordionQuestionListItem>Автоинформатор</Styled.AccordionQuestionListItem>
              <Styled.AccordionQuestionListItem>Автоперезвон по пропущенным</Styled.AccordionQuestionListItem>
              <Styled.AccordionQuestionListItem>Распределение по менеджерам</Styled.AccordionQuestionListItem>
            </Styled.AccordionQuestionList>
          </Styled.AccordionQuestionListContainer>
      )
    },
    {
      title: 'Могу ли я получить скидку?',
      text: 'Да, можете. Для клиентов с 15+ пользователями на аккаунте мы предоставляем специальные цены. Оставьте заявку и наш менеджер согласует с Вами индивидуальные условия.'
    },
  ]

  return (
    <Screen
      id='price'
      title={<span><StyledScreen.GreenText>Сколько стоит</StyledScreen.GreenText> onlinepbx</span>}
      bottomLine={true}
      style={props.style || {}}
      {...props.screenProps}
    >
      <Styled.Container>
        <Styled.Left largeWidth={country?.value === 'UZ'}>
          <Styled.BlockTitle>Рабочее место</Styled.BlockTitle>
          <Styled.InputBlock>
            <Styled.CalculateBlock>
              <Styled.IncDecBtn onClick={handleMinusCount}>
                <MinusIcon />
              </Styled.IncDecBtn>
              <Styled.UsersCountInput
                ref={input}
                onKeyUp={handlePress}
                value={(Number.isInteger(countUser) && !isFocus && countUser) || countUser || ''}
                onChange={(evt: any) => {
                  if (+evt.target.value > MAX_COUNT_USERS) return handleChange(MAX_COUNT_USERS)
                  evt.target.value.match(reg) && handleChange(+evt.target.value)
                }}
                onFocus={handleFocus}
                onBlur={() => {
                  if (countUser === 0) {
                    setCount(MIN_COUNT_USERS)
                  }
                  if (countUser > MAX_COUNT_USERS) {
                    setCount(MAX_COUNT_USERS)
                  }
                  handleFocus()
                }}
              />
              <Styled.IncDecBtn onClick={handlePlusCount}>
                <PlusIcon />
              </Styled.IncDecBtn>
            </Styled.CalculateBlock>
            <Styled.PriceText>
              <Styled.MultSign><MultSignIcon /></Styled.MultSign>
              {dividedByThree(pricePerUser, ' ')} {currency}
            </Styled.PriceText>
          </Styled.InputBlock>
          <Styled.AdditionalModules>
            <Styled.BlockTitle>Дополнительные модули</Styled.BlockTitle>
            <Styled.AdditionalModulesList>
              {
                ADDITIONAL_MODULES.map((module: any, index) => (
                  <Styled.AdditionalModule key={index}>
                    <Styled.ModuleCheck>
                      <OnlinepbxCheckbox
                        checked={selectedModules[module.label]}
                        questionText={module.promptText}
                        questionProps={{ openModalByHover: true }}
                        label={module.title}
                        onClickChecked={() => onSelectModule(module)}
                      />
                    </Styled.ModuleCheck>
                    <Styled.ModuleCost>{dividedByThree(getPrice(module.label, services, country, isLegalPerson), ' ')} {currency}</Styled.ModuleCost>
                  </Styled.AdditionalModule>
                ))
              }
            </Styled.AdditionalModulesList>
          </Styled.AdditionalModules>
        </Styled.Left>
        <Styled.Right largeWidth={country?.value === 'UZ'}>
          <Styled.IconWrapp>
            <Styled.IconBg />
              {country?.value === 'UZ'
                ? <Styled.IconPlaceUZ>
                    <Styled.ResultPrice smallFont={calculate > 100000}>{`${dividedByThree(calculate, ' ')} ${currency}`}</Styled.ResultPrice>
                    <Styled.PeriodText>в месяц</Styled.PeriodText>
                  </Styled.IconPlaceUZ>
                : <Styled.IconPlace>
                    <Styled.ResultPriceBold smallFont={calculate > 100000}>{`${dividedByThree(Math.round(calculate), ' ')}${country?.value === 'RU' ? '*' : ''} ${currency}`}
                    </Styled.ResultPriceBold>
                    <Styled.PeriodText>в месяц</Styled.PeriodText>
                  </Styled.IconPlace>}
          </Styled.IconWrapp>
        </Styled.Right>
      </Styled.Container>
      <Styled.Accordion>
        <Accordion
          data={questions}
          initialElemIndex={activeQuestionIndex}
          onChange={(index: number) => {
            setActiveQuestionIndex(index)
          }}
        />
        {country?.value === 'RU' && <Styled.Footnote>*цена для новых клиентов, зарегистрированных в сервисе с 14.01.2025</Styled.Footnote>}
      </Styled.Accordion>
    </Screen>
  )
}

export { PriceCalculateScreenV2 }
